import { estadoNoPlugin } from './trocaDasTelas';

/**
 * "ATUALIZAÇÃO PRONTA · APLICAR AGORA" (1.4, 23/10/2026, pedido do pesquisador). O atualizador baixa o pacote e o deixa para
 * a PRÓXIMA ABERTURA (`trocaNaAbertura.ts`); para a equipe, o app oferece aplicar na hora.
 *
 * Quando aparece (`deveMostrarAviso`): só no app; só para a equipe (nunca aluno); fora da página da prova; com pacote
 * baixado e anotado; e SEM prova guardada no aparelho (a mesma regra da abertura, com as cópias órfãs descartadas —
 * `conferirCopiasDoAparelho`). Tocar confere a prova de novo e aplica (`aplicarAgora`): a mesma sequência da abertura.
 */
export const EVENTO_TELAS_PRONTAS = 'telas:prontas';

export type Pendente = { versao: string; abertura: string } | null;
export function deveMostrarAviso(p: { nativo: boolean; papel?: string | null; foraDaProva: boolean; pendente: Pendente; baixado: boolean; provaGuardada: boolean }): boolean {
  return p.nativo && !!p.papel && p.papel !== 'STUDENT' && p.foraDaProva && !!p.pendente && p.baixado && !p.provaGuardada;
}

export type Dependencias = {
  lerPendente: () => Promise<Pendente>;
  anotarPendente: (p: Pendente) => Promise<void>;
  listar: () => Promise<{ bundles: Array<{ id: string; version: string; status: string }> }>;
  cancelarAtraso: () => Promise<unknown>;
  aplicar: (id: string) => Promise<unknown>;
  provaGuardada: () => Promise<boolean>;
};

/**
 * As dependências de verdade (o plugin e o armazenamento nativo). No build da web de TESTE (`VITE_COFRE_DE_TESTE=1`, o
 * "aparelho de mentira" dos roteiros de tela) a página pode trocá-las por `window.__atualizacaoDeTeste`; no build de
 * produção a variável não existe, e o ramo some.
 */
export async function dependenciasPadrao(): Promise<Dependencias> {
  const deTeste = import.meta.env.VITE_COFRE_DE_TESTE === '1' && (window as unknown as { __atualizacaoDeTeste?: Dependencias }).__atualizacaoDeTeste;
  if (deTeste) return deTeste;
  const { lerPendente, anotarPendente } = await import('./canalDoAparelho');
  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
  const { conferirCopiasDoAparelho } = await import('./trocaNaAbertura');
  return {
    lerPendente: () => lerPendente(),
    anotarPendente: (p) => anotarPendente(p),
    listar: () => CapacitorUpdater.list() as never,
    cancelarAtraso: () => CapacitorUpdater.cancelDelay().catch(() => undefined),
    aplicar: (id) => CapacitorUpdater.set({ id }),
    provaGuardada: async () => (await conferirCopiasDoAparelho()).provaGuardada,
  };
}

/** O pacote pendente e baixado (o id dele no plugin), ou nulo. */
export async function pacotePronto(d: Dependencias): Promise<{ versao: string; id: string } | null> {
  const pendente = await d.lerPendente();
  if (!pendente) return null;
  const b = (await d.listar()).bundles.find((x) => x.version === pendente.versao && estadoNoPlugin(x.status) === 'baixado');
  return b ? { versao: pendente.versao, id: b.id } : null;
}

/** Aplica agora: a prova de novo (pode ter aberto uma), tira a anotação, desfaz o atraso antigo e troca (recarrega a página). */
export async function aplicarAgora(d: Dependencias): Promise<'aplicando' | 'prova' | 'sem-pacote'> {
  const pacote = await pacotePronto(d);
  if (!pacote) return 'sem-pacote';
  if (await d.provaGuardada()) return 'prova';
  await d.anotarPendente(null);
  await d.cancelarAtraso();
  await d.aplicar(pacote.id);
  return 'aplicando';
}
